/**
 * Color utility functions for contrast and accessibility
 */

/**
 * Parses a color string (hex, rgb, rgba) and returns RGB values
 * @param color - Color string in hex (#RGB, #RRGGBB) or rgb/rgba format
 * @returns Object with r, g, b values (0-255) or null if invalid
 */
export function parseColor(
  color: string
): { r: number; g: number; b: number } | null {
  if (!color) return null;

  // Handle hex colors
  if (color.startsWith("#")) {
    let hex = color.slice(1);

    // Convert 3-char hex to 6-char
    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }

    if (hex.length !== 6) return null;

    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    if (isNaN(r) || isNaN(g) || isNaN(b)) return null;

    return { r, g, b };
  }

  // Handle rgb/rgba colors
  const rgbMatch = color.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgbMatch) {
    return {
      r: parseInt(rgbMatch[1], 10),
      g: parseInt(rgbMatch[2], 10),
      b: parseInt(rgbMatch[3], 10),
    };
  }

  return null;
}

/**
 * Calculates the relative luminance of a color
 * Based on WCAG 2.0 formula: https://www.w3.org/TR/WCAG20/#relativeluminancedef
 * @param r - Red value (0-255)
 * @param g - Green value (0-255)
 * @param b - Blue value (0-255)
 * @returns Relative luminance (0-1)
 */
export function getRelativeLuminance(r: number, g: number, b: number): number {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    const sRGB = c / 255;
    return sRGB <= 0.03928
      ? sRGB / 12.92
      : Math.pow((sRGB + 0.055) / 1.055, 2.4);
  });

  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/** Relative luminance of a colour string; `null` when it cannot be parsed. */
function luminanceOf(color: string): number | null {
  const parsed = parseColor(color);
  if (!parsed) return null;
  return getRelativeLuminance(parsed.r, parsed.g, parsed.b);
}

/**
 * WCAG 2.x contrast ratio between two colours, 1 (identical) to 21 (black on
 * white). Returns `null` if either colour cannot be parsed.
 *
 * @example
 * getContrastRatio("#ffffff", "#000000") // 21
 */
export function getContrastRatio(
  colorA: string,
  colorB: string
): number | null {
  const a = luminanceOf(colorA);
  const b = luminanceOf(colorB);
  if (a === null || b === null) return null;
  const lighter = Math.max(a, b);
  const darker = Math.min(a, b);
  return (lighter + 0.05) / (darker + 0.05);
}

/** WCAG AA for normal-size text. */
export const AA_CONTRAST_RATIO = 4.5;

/**
 * Determines the best text color (dark or light) for maximum contrast
 * against a given background color.
 *
 * Uses WCAG relative luminance to determine if the background is light or dark,
 * then returns the appropriate text color for maximum readability.
 *
 * MIN-CONTRAST CLAMP. The soft defaults (`#1a1a1a` / `#ffffff`) are the house
 * text colours, and for most backgrounds one of them clears AA comfortably.
 * They cannot both clear it for a MID-TONE background, though: against a
 * background whose relative luminance sits around 0.20 the better of the two
 * tops out at ~4.17:1, so a user-chosen mid-tone label colour rendered a chip
 * that failed AA whichever way the picker fell. Where that happens this falls
 * through to PURE black / white, whose worst case across the whole luminance
 * range is 4.58:1 — so the returned colour always clears AA on a parseable
 * background. Softened text is preferred whenever it is legible; the pure
 * value is a floor, not the default.
 *
 * @param backgroundColor - Background color in hex (#RGB, #RRGGBB) or rgb format
 * @param darkColor - Color to use for dark text (default: "#1a1a1a")
 * @param lightColor - Color to use for light text (default: "#ffffff")
 * @returns The appropriate text color for maximum contrast
 *
 * @example
 * // Light background -> dark text
 * getContrastTextColor("#E7F8F2") // Returns "#1a1a1a"
 *
 * @example
 * // Dark background -> light text
 * getContrastTextColor("#BD10E0") // Returns "#ffffff"
 *
 * @example
 * // Mid-tone background -> neither soft default clears AA, so pure black
 * getContrastTextColor("#808080") // Returns "#000000"
 *
 * @example
 * // Custom colors
 * getContrastTextColor("#4F31FF", "#000000", "#FFFFFF")
 */
export function getContrastTextColor(
  backgroundColor: string,
  darkColor: string = "#1a1a1a",
  lightColor: string = "#ffffff"
): string {
  const luminance = luminanceOf(backgroundColor);

  if (luminance === null) {
    // Default to dark text if color cannot be parsed
    return darkColor;
  }

  // WCAG recommends a contrast ratio of at least 4.5:1 for normal text
  // Using 0.179 as the threshold (roughly middle of the luminance scale)
  // accounts for both light and dark backgrounds
  const preferred = luminance > 0.179 ? darkColor : lightColor;

  const preferredRatio = getContrastRatio(backgroundColor, preferred);
  if (preferredRatio !== null && preferredRatio >= AA_CONTRAST_RATIO) {
    return preferred;
  }

  // The soft default does not clear AA here. Try the other soft default before
  // reaching for a pure value — it may be the better of the two on a colour
  // that sits near the threshold.
  const alternate = preferred === darkColor ? lightColor : darkColor;
  const alternateRatio = getContrastRatio(backgroundColor, alternate);
  if (alternateRatio !== null && alternateRatio >= AA_CONTRAST_RATIO) {
    return alternate;
  }

  // Mid-tone background: clamp to the pure value with the higher ratio.
  return luminance > 0.1791 ? "#000000" : "#ffffff";
}

/**
 * Checks if a color is considered "light" (would need dark text for contrast)
 * @param color - Color string in hex or rgb format
 * @returns true if the color is light, false if dark
 */
export function isLightColor(color: string): boolean {
  const parsed = parseColor(color);
  if (!parsed) return true; // Default to treating as light

  const luminance = getRelativeLuminance(parsed.r, parsed.g, parsed.b);
  return luminance > 0.179;
}

/**
 * Returns Tailwind-compatible class name for contrasting text
 * @param backgroundColor - Background color in hex or rgb format
 * @returns "text-neutral-900" for light backgrounds, "text-white" for dark
 */
export function getContrastTextClass(backgroundColor: string): string {
  return isLightColor(backgroundColor) ? "text-neutral-900" : "text-white";
}
